// 조치 이력 ↔ 고객사 리포트 **수동 연결** 창.
//
// 자동 매칭은 리포트 번호가 1순위고, 없으면 매장명+시간대로 맞춘다. 둘 다 안 되면
// (시트에 번호가 없고 매장명도 안 맞는 경우) 사람이 이어 줘야 한다.
//
// 종전에는 로봇 상세의 '연결하기' 가 조치 이력 목록으로 보내기만 했다 — 거기서
// 연필을 눌러 리포트 번호를 **손으로 타이핑**해야 했고, 어떤 리포트인지 보지도
// 못한 채 번호만 적는 꼴이었다(오너 신고 2026-09-16).
//
// 그래서 **고르고 → 확인하고 → 잇는다** 로 바꿨다. 고른 리포트를 확인 단계에서
// 목록과 **같은 모양으로** 다시 보여 준다 — 번호만 보여 주면 잘못 골라도 모른다.

import { useMemo, useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  kstDateTime,
  SERVICE_ORDER_STATUS_LABELS,
  SERVICE_SEVERITY_LABELS,
  sheetOutcomeLabel,
  type CustomerSheetReportDto,
} from '@bhops/shared';
import { customerSheetReportsApi } from '@/api/customerSheetReports';
import { faultRemediationsApi } from '@/api/faultRemediations';
import { extractApiError } from '@/api/client';
import { useToast } from '@/components/Toast';
import { useBackdropDismiss } from '@/components/use-backdrop-dismiss';
import { pill, SEVERITY_TONE, SHEET_STATUS_TONE, SHEET_STATUS_TONE_UNKNOWN } from './field-tones';

const control: CSSProperties = {
  padding: '6px 8px',
  borderRadius: 'var(--r-input)',
  border: '1px solid var(--border-default)',
  fontSize: 13,
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
};

const backdropStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.35)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 100,
};

const panelStyle: CSSProperties = {
  background: 'var(--bg-surface)',
  borderRadius: 'var(--r-card)',
  padding: 20,
  width: 720,
  maxWidth: '92vw',
  maxHeight: '86vh',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

/**
 * 리포트 한 줄 — 목록과 확인 단계가 **같은 모양**을 쓴다.
 *
 * 고객사 리포트 목록(CustomerSheetReportsPage)과 같은 어휘·색이다. 사람이 그
 * 화면에서 익힌 읽는 법을 여기서 다시 배우지 않아도 된다.
 */
function ReportRow({ r }: { r: CustomerSheetReportDto }) {
  const tone = SEVERITY_TONE[r.severity];
  const outcome = sheetOutcomeLabel(r.sheetOutcome);
  return (
    <span
      style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', width: '100%' }}
    >
      <span style={{ ...pill, background: tone.bg, color: tone.fg }}>
        {SERVICE_SEVERITY_LABELS[r.severity]}
      </span>
      <span style={{ fontWeight: 600, color: 'var(--fg-brand)', whiteSpace: 'nowrap' }}>
        {r.sheetRowKey}
      </span>
      <span style={{ whiteSpace: 'nowrap' }}>{r.storeName?.trim() || '–'}</span>
      <span style={{ ...pill, ...(SHEET_STATUS_TONE[r.status] ?? SHEET_STATUS_TONE_UNKNOWN) }}>
        {SERVICE_ORDER_STATUS_LABELS[r.status]}
      </span>
      {outcome && (
        <span style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
          {outcome}
        </span>
      )}
      <span style={{ flex: 1, minWidth: 160 }}>{r.summary}</span>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
        {kstDateTime(r.receivedAt) ?? '–'}
      </span>
    </span>
  );
}

export function ReportLinkPicker({
  remediation,
  onClose,
  onLinked,
}: {
  remediation: { id: string; symptom: string; storeName: string | null; occurredAt: string };
  onClose: () => void;
  onLinked: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  // 처음에는 **전부 보여 준다.** 검색은 좁히는 용도다(오너 결정 2026-09-16).
  //
  // 종전에는 매장명으로 미리 좁혀서 열었다. 같은 매장일 확률이 높다는 이유였는데,
  // 시트의 매장명은 자유 입력이라(`테스트` vs `테스트xx`) 조금만 달라도 정작 이어야
  // 할 리포트가 **처음부터 안 보였다.** 목록이 짧으면 눈으로 찾는 게 빠르고,
  // 길면 검색하면 된다 — 안 보이는 것보다 낫다.
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  // 고른 리포트. null 이면 아직 고르는 중이고, 값이 있으면 확인 단계다.
  const [picked, setPicked] = useState<CustomerSheetReportDto | null>(null);

  const listQ = useQuery({
    queryKey: ['report-link-picker', applied],
    queryFn: () => customerSheetReportsApi.list({ search: applied || undefined, pageSize: 50 }),
  });

  const linkMutation = useMutation({
    mutationFn: (report: CustomerSheetReportDto) =>
      faultRemediationsApi.update(remediation.id, { matchedReportNo: report.sheetRowKey }),
    onSuccess: (_data, report) => {
      // 두 표가 같이 바뀐다 — 조치 이력은 연결이, 리포트는 상태가(전파 규칙).
      void qc.invalidateQueries({ queryKey: ['robot-fault-remediations'] });
      void qc.invalidateQueries({ queryKey: ['robot-sheet-reports'] });
      void qc.invalidateQueries({ queryKey: ['fault-remediations'] });
      void qc.invalidateQueries({ queryKey: ['customer-sheet-reports'] });
      toast.success(`리포트 ${report.sheetRowKey}에 연결했습니다.`, '연결 완료');
      onLinked();
    },
    onError: (e) => toast.error(extractApiError(e), '연결 실패'),
  });

  const items = useMemo(() => listQ.data?.items ?? [], [listQ.data]);
  const busy = linkMutation.isPending;
  const dismiss = useBackdropDismiss(onClose);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="리포트 연결"
      {...dismiss}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
      style={backdropStyle}
    >
      <div style={panelStyle}>
        <h2 className="kuun-h3" style={{ margin: 0 }}>
          {picked ? '리포트 연결 확인' : '연결할 리포트 고르기'}
        </h2>

        {/* 무엇을 잇는지 — 조치 쪽을 늘 띄워 둔다. 리포트만 보고 고르면
            "어느 조치에 붙이는 거였지" 를 놓친다. */}
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          조치: {kstDateTime(remediation.occurredAt) ?? '–'}
          {remediation.storeName ? ` · ${remediation.storeName}` : ''} · {remediation.symptom}
        </div>

        {picked ? (
          <>
            {/* 확인 단계 — 고른 리포트를 목록과 같은 모양으로 다시 보여 준다.
                번호만 보여 주면 잘못 골랐어도 알아챌 수가 없다(오너 요청). */}
            <div
              data-testid="report-link-confirm-row"
              style={{
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--r-card)',
                padding: 12,
                background: 'var(--bg-subtle)',
              }}
            >
              <ReportRow r={picked} />
            </div>
            <p style={{ margin: 0, fontSize: 14 }}>해당 리포트와 연결하겠습니까?</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                onClick={() => setPicked(null)}
                disabled={busy}
                style={{ ...control, cursor: 'pointer' }}
              >
                다시 고르기
              </button>
              <button
                type="button"
                onClick={() => linkMutation.mutate(picked)}
                disabled={busy}
                data-testid="report-link-confirm"
                style={{
                  ...control,
                  background: 'var(--fg-brand)',
                  // 브랜드 바탕 위 글씨는 `--bg-surface` 다(같은 화면의 저장 버튼 둘과
                  // 같은 토큰). 종전엔 `--fg-on-brand` 를 썼는데 **없는 토큰**이라
                  // 기본 글자색(어두운색)이 그대로 나왔다.
                  color: 'var(--bg-surface)',
                  border: '1px solid var(--fg-brand)',
                  fontWeight: 600,
                  cursor: busy ? 'default' : 'pointer',
                }}
              >
                {busy ? '연결 중…' : '연결'}
              </button>
            </div>
          </>
        ) : (
          <>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setApplied(search.trim());
              }}
              style={{ display: 'flex', gap: 8 }}
            >
              <input
                style={{ ...control, flex: 1 }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="요약, 매장, 리포트 번호로 검색"
                aria-label="리포트 검색"
              />
              <button type="submit" style={{ ...control, cursor: 'pointer' }}>
                검색
              </button>
            </form>

            {listQ.isLoading ? (
              <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>불러오는 중…</p>
            ) : listQ.isError ? (
              <p style={{ color: 'var(--fg-danger)', fontSize: 13 }}>
                리포트를 불러오지 못했습니다.
              </p>
            ) : items.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                맞는 리포트가 없습니다. 검색어를 지우면 최근 것부터 전부 보입니다.
              </p>
            ) : (
              <ul
                style={{
                  listStyle: 'none',
                  margin: 0,
                  padding: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {items.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => setPicked(r)}
                      data-testid={`report-link-option-${r.sheetRowKey}`}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: '1px solid var(--border-subtle)',
                        padding: '10px 4px',
                        cursor: 'pointer',
                        fontSize: 13,
                        color: 'var(--fg-default)',
                      }}
                    >
                      <ReportRow r={r} />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" onClick={onClose} style={{ ...control, cursor: 'pointer' }}>
                취소
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
